import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, AlertTriangle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Displays preview of import operations with validation results
 * @param {Object} props
 * @param {Object} props.preview - Preview data with toAdd, toUpdate arrays
 * @param {Array} props.errors - Array of error objects
 * @param {Array} props.warnings - Array of warning objects
 */
export function ImportPreview({ preview, errors = [], warnings = [] }) {
  const [expandedUpdates, setExpandedUpdates] = useState({});

  // Show component if we have preview data OR errors/warnings to display
  if (!preview && errors.length === 0 && warnings.length === 0) {
    return null;
  }

  const { toAdd = [], toUpdate = [], summary = {} } = preview || {};
  const totalRows = summary.totalRows || toAdd.length + toUpdate.length;
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;

  const toggleUpdate = (index) => {
    setExpandedUpdates(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Rows
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalRows}</div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-green-700">
              New Elements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{toAdd.length}</div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-blue-700">
              Updates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{toUpdate.length}</div>
          </CardContent>
        </Card>

        <Card className={hasErrors ? "border-red-200 bg-red-50" : ""}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-sm font-medium ${hasErrors ? 'text-red-700' : 'text-muted-foreground'}`}>
              Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${hasErrors ? 'text-red-700' : ''}`}>
              {errors.length}
            </div>
          </CardContent>
        </Card>

        <Card className={hasWarnings ? "border-yellow-200 bg-yellow-50" : ""}>
          <CardHeader className="pb-3">
            <CardTitle className={`text-sm font-medium ${hasWarnings ? 'text-yellow-700' : 'text-muted-foreground'}`}>
              Warnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${hasWarnings ? 'text-yellow-700' : ''}`}>
              {warnings.length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Errors Alert */}
      {hasErrors && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {errors.length} validation error{errors.length !== 1 ? 's' : ''} found. Please fix these issues before importing.
          </AlertDescription>
        </Alert>
      )}

      {/* Warnings Alert */}
      {hasWarnings && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            {warnings.length} warning{warnings.length !== 1 ? 's' : ''} found. These won't prevent import but should be reviewed.
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs for Different Sections */}
      <Tabs defaultValue={hasErrors ? "errors" : "new"} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="new" className="relative">
            New Elements
            {toAdd.length > 0 && (
              <Badge variant="secondary" className="ml-2">{toAdd.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="updates" className="relative">
            Updates
            {toUpdate.length > 0 && (
              <Badge variant="secondary" className="ml-2">{toUpdate.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="errors" className="relative">
            Errors
            {hasErrors && (
              <Badge variant="destructive" className="ml-2">{errors.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="warnings" className="relative">
            Warnings
            {hasWarnings && (
              <Badge variant="secondary" className="ml-2 bg-yellow-100 text-yellow-800">{warnings.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* New Elements Tab */}
        <TabsContent value="new" className="space-y-4">
          {toAdd.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  <Info className="h-8 w-8 mx-auto mb-2" />
                  <p>No new elements to add</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[180px]">Canonical Name</TableHead>
                      <TableHead className="min-w-[200px]">Definition</TableHead>
                      <TableHead className="w-[100px]">Data Type</TableHead>
                      <TableHead className="w-[80px]">Status</TableHead>
                      <TableHead className="min-w-[150px]">Business Summary</TableHead>
                      <TableHead className="w-[130px]">Reference List</TableHead>
                      <TableHead className="min-w-[130px]">Processing Rules</TableHead>
                      <TableHead className="w-[100px]">Language</TableHead>
                      <TableHead className="w-[60px]">Version</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {toAdd.map((item, index) => {
                      const ruleNames = Array.isArray(item.processing_rules) ? item.processing_rules
                        : typeof item.processing_rules === 'string' && item.processing_rules.trim()
                          ? item.processing_rules.split(',').map(s => s.trim()).filter(Boolean)
                          : [];
                      return (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{item.canonical_name}</TableCell>
                          <TableCell>
                            <span className="text-sm max-w-[200px] truncate block" title={item.definition}>
                              {item.definition || '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.data_type || 'string'}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.status || 'DRAFT'}</Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm max-w-[150px] truncate block" title={item.business_summary}>
                              {item.business_summary || '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{item.reference_list_name || '-'}</span>
                          </TableCell>
                          <TableCell>
                            {ruleNames.length > 0 ? (
                              <span className="text-sm" title={ruleNames.join(', ')}>
                                {ruleNames.length} rule{ruleNames.length !== 1 ? 's' : ''}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{item.processing_language || '-'}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">{item.version_number || '-'}</span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Updates Tab */}
        <TabsContent value="updates" className="space-y-4">
          {toUpdate.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  <Info className="h-8 w-8 mx-auto mb-2" />
                  <p>No existing elements to update</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {toUpdate.map((item, index) => (
                <Card key={index} className="border-blue-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-blue-600" />
                        <CardTitle className="text-base">{item.canonical_name}</CardTitle>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleUpdate(index)}
                      >
                        {expandedUpdates[index] ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  {expandedUpdates[index] && (
                    <CardContent className="space-y-3">
                      {item.changes && item.changes.map((change, changeIndex) => (
                        <div key={changeIndex} className="space-y-1">
                          <div className="text-sm font-medium text-muted-foreground">
                            {change.field}:
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="p-2 bg-red-50 border border-red-200 rounded">
                              <div className="text-xs text-red-600 font-medium mb-1">Old:</div>
                              <div className="text-red-900">{change.oldValue || '(empty)'}</div>
                            </div>
                            <div className="p-2 bg-green-50 border border-green-200 rounded">
                              <div className="text-xs text-green-600 font-medium mb-1">New:</div>
                              <div className="text-green-900">{change.newValue || '(empty)'}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {!item.changes && (
                        <div className="text-sm text-muted-foreground">
                          No changes detected (values are identical)
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Errors Tab */}
        <TabsContent value="errors" className="space-y-4">
          {errors.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-green-600">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>No validation errors found</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Column</TableHead>
                      <TableHead>Error</TableHead>
                      <TableHead>Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {errors.map((error, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {error.row || '-'}
                        </TableCell>
                        <TableCell>{error.column || '-'}</TableCell>
                        <TableCell className="text-red-600">{error.message}</TableCell>
                        <TableCell className="max-w-xs truncate font-mono text-xs">
                          {error.value !== undefined ? String(error.value) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Warnings Tab */}
        <TabsContent value="warnings" className="space-y-4">
          {warnings.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-green-600">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>No warnings</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Column</TableHead>
                      <TableHead>Warning</TableHead>
                      <TableHead>Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {warnings.map((warning, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {warning.row || '-'}
                        </TableCell>
                        <TableCell>{warning.column || '-'}</TableCell>
                        <TableCell className="text-yellow-700">{warning.message}</TableCell>
                        <TableCell className="max-w-xs truncate font-mono text-xs">
                          {warning.value !== undefined ? String(warning.value) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
